import { useState } from "react";
import { Checkbox } from "@chakra-ui/react";
import { cn } from "utils/helpers";
import type { EvaluationPanelMember } from "../../../types/panel.type";
import { useCurrentUserId } from "hooks/auth";
import { MemberStatusBadge, MemberActionsMenu, MemberDisclaimerTableCell } from "../../PanelMembers";
import { ConflictReviewModal } from "../../ConflictReviewModal";

interface PanelMemberTableProps {
	canManage?: boolean;
	/** When true, renders the Disclaimer column and conflict review flow. */
	disclaimerEnabled?: boolean;
	evalId: number;
	members: EvaluationPanelMember[];
	rfxId: number;
	/** Ids of selected (pending) members, used for the Send Invitations flow. */
	selectedIds?: number[];
	/** Called after a member action mutates the panel, so the list can refresh. */
	onMemberChanged?: () => void;
	onToggleSelect?: (memberId: number) => void;
	onToggleSelectAll?: (selectAll: boolean) => void;
}

type ConflictModalState = {
	member: EvaluationPanelMember;
	mode: "review" | "view";
} | null;

const getTypeBadgeClasses = (memberType: EvaluationPanelMember["member_type"]): string => {
	switch (memberType) {
		case "internal":
			return "bg-blue-100 text-blue-700";
		case "external":
			return "bg-amber-100 text-amber-700";
		case "ai":
			return "bg-purple-100 text-purple-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

export const PanelMemberTable: React.FC<PanelMemberTableProps> = ({
	members,
	rfxId,
	evalId,
	canManage = false,
	disclaimerEnabled = false,
	selectedIds = [],
	onToggleSelect,
	onToggleSelectAll,
	onMemberChanged,
}) => {
	const currentUserId = useCurrentUserId();
	const [conflictModal, setConflictModal] = useState<ConflictModalState>(null);

	const pendingMembers = members.filter((m) => m.status === "pending");
	const showSelection = canManage && pendingMembers.length > 0 && !!onToggleSelect;
	const allPendingSelected =
		pendingMembers.length > 0 && pendingMembers.every((m) => selectedIds.includes(m.id));

	if (members.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm"
				data-testid="panel-members-empty"
			>
				No panel members added yet. Use the inputs below to add members.
			</div>
		);
	}

	return (
		<div className="w-full overflow-x-auto" data-testid="panel-members-table">
			<table className="w-full min-w-[600px] border-collapse text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
						{showSelection && (
							<th className="w-10 px-3 py-3">
								<Checkbox
									isChecked={allPendingSelected}
									onChange={(e) => onToggleSelectAll?.(e.target.checked)}
									data-testid="select-all-pending-checkbox"
									aria-label="Select all pending members"
								/>
							</th>
						)}
						<th className="px-3 py-3">Status</th>
						{disclaimerEnabled && (
							<th className="px-3 py-3" data-testid="disclaimer-column-header">
								Disclaimer
							</th>
						)}
						<th className="px-3 py-3">Name</th>
						<th className="px-3 py-3">Email</th>
						<th className="px-3 py-3">Type</th>
						{canManage && <th className="px-3 py-3 text-right">Actions</th>}
					</tr>
				</thead>
				<tbody>
					{members.map((member) => {
						const isPending = member.status === "pending";
						const isDeclined = member.status === "declined";
						const isChecked = selectedIds.includes(member.id);
						const isCurrent = currentUserId === member.user_id;
						const hasConflict = member.disclaimer_status === "conflict";
						const isReviewable = hasConflict && canManage;
						return (
							<tr
								key={member.id}
								className={cn(
									"border-b border-gray-100 transition-colors hover:bg-gray-50",
									isDeclined && "bg-gray-50/50 text-gray-500",
									hasConflict && "bg-red-50",
									isReviewable && "cursor-pointer"
								)}
								data-testid={`panel-member-row-${member.id}`}
								data-status={member.status}
								onClick={
									isReviewable ? () => setConflictModal({ member, mode: "review" }) : undefined
								}
							>
								{showSelection && (
									<td className="px-3 py-3">
										{isPending && onToggleSelect && (
											<Checkbox
												isChecked={isChecked}
												onChange={() => onToggleSelect(member.id)}
												data-testid={`select-member-checkbox-${member.id}`}
											/>
										)}
									</td>
								)}
								<td className="px-3 py-3">
									<MemberStatusBadge status={member.status} className="capitalize" />
								</td>
								{disclaimerEnabled && (
									<td className="px-3 py-3">
										<MemberDisclaimerTableCell member={member} />
									</td>
								)}
								<td className="px-3 py-3 font-medium text-gray-900">
									{member.name || member.user?.full_name || "-"}{isCurrent && " (you)"}
								</td>
								<td className="px-3 py-3 text-gray-600">
									{member.email}
								</td>
								<td className="px-3 py-3">
									<span
										className={cn(
											"inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
											getTypeBadgeClasses(member.member_type)
										)}
									>
										{member.member_type}
									</span>
								</td>
								{canManage && (
									<td
										className="px-3 py-3"
										onClick={(e) => e.stopPropagation()}
									>
										<div className="flex items-center justify-end">
											<MemberActionsMenu
												rfxId={rfxId}
												evalId={evalId}
												member={{
													id: member.id,
													status: member.status,
													name: member.name,
													email: member.email,
													disclaimerStatus: member.disclaimer_status,
												}}
												onChanged={onMemberChanged}
												onReviewConflict={
													member.disclaimer_status === "conflict"
														? () => setConflictModal({ member, mode: "review" })
														: undefined
												}
												onViewConflict={
													member.disclaimer_status === "reviewed"
														? () => setConflictModal({ member, mode: "view" })
														: undefined
												}
											/>
										</div>
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
			</table>

			{conflictModal && (
				<ConflictReviewModal
					isOpen
					onClose={() => setConflictModal(null)}
					onReviewed={onMemberChanged}
					rfxId={rfxId}
					evalId={evalId}
					memberId={conflictModal.member.id}
					memberName={conflictModal.member.name || conflictModal.member.user?.full_name || "-"}
					memberEmail={conflictModal.member.email}
					conflictDetails={conflictModal.member.disclaimer_conflict_details ?? ""}
					mode={conflictModal.mode}
					reviewAction={conflictModal.member.disclaimer_review_action}
					reviewNotes={conflictModal.member.disclaimer_review_notes}
					reviewedAt={conflictModal.member.disclaimer_reviewed_at}
				/>
			)}
		</div>
	);
};
